import { useQuery } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import * as React from 'react';
import tw from 'twin.macro';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useWorkersService } from '@restworld/data-services';
import { Spinner, Title } from '@restworld/ui-ds';
import { WorkerJPSavedResearch } from '@restworld/utility-types';
import { useVariableOptions } from '@restworld/utils-common';

import SavedResearch from './saved-research';
import SavedResearchNotificationChangeModal from './save-research-notification-change-modal';
import { notificationResearchIdAtom } from './save-research-tab.atoms';

const SaveResearchTab = () => {
  const { fetchVariable, variableOptions } = useVariableOptions();
  const workerServices = useWorkersService();

  const listSaveResearch = React.useCallback(
    async () =>
      (
        await workerServices.listSavedResearches({
          limit: 999,
          offset: 0,
        })
      ).data,
    [workerServices]
  );

  useEffect(() => {
    fetchVariable(['domain_interest']);
  }, [fetchVariable]);

  const { data, isFetching } = useQuery({
    queryKey: ['listSaveResearch'],
    queryFn: listSaveResearch,
    refetchOnWindowFocus: false,
  });

  const savedResearches = useMemo(
    () =>
      data
        ?.sort((a: WorkerJPSavedResearch, b: WorkerJPSavedResearch) => b.id - a.id)
        .map((item) => {
          return (
            <li key={item.id}>
              <SavedResearch data={{ ...item }} domainInterest={variableOptions['domain_interest']} />
            </li>
          );
        }),
    [data, variableOptions]
  );

  const saveResearchId = useAtomValue(notificationResearchIdAtom);
  const { t } = useTranslation();
  return (
    <div tw="flex flex-col gap-10 lg:(flex-row justify-between)">
      <div tw="flex flex-col gap-2">
        <Title
          content={t('user-settings:saved_searches')}
          fontWeight="semibold"
          fontSize="extraLarge"
          twStyle={tw`leading-7.5`}
        />
        <Title
          content={t('user-settings:opt_for_notifications_or_deleting_saved_searches')}
          fontWeight="normal"
          fontSize="small"
          twStyle={tw`leading-5.25`}
          textColor="gray-600"
        />
      </div>
      <div tw="flex flex-col gap-8 lg:w-1/2">
        <div tw="self-end lg:pr-8">
          <Title
            content={t('common:notifications')}
            textColor="gray-400"
            fontWeight="medium"
            fontSize="small"
            twStyle={tw`leading-5.25`}
          />
        </div>
        <ul tw="flex flex-col gap-8 lg:(max-h-104 overflow-y-auto pr-2)">
          {isFetching && !data?.length && (
            <li key="loading">
              <Spinner color="primary" size="lg" />
            </li>
          )}
          {savedResearches}
          <SavedResearchNotificationChangeModal key={saveResearchId} />
        </ul>
      </div>
    </div>
  );
};

export default SaveResearchTab;
